// Ticket vocabulary and action rules — triage, not approval, so purely
// permission-based (its_view, its_act, its_triage, its_agent).

export const ITS_VIEW_PERMISSIONS = ['rbacapp.its_view', 'rbacapp.its_act', 'rbacapp.its_triage', 'rbacapp.its_agent'];
export const ITS_ACT_PERMISSIONS = ['rbacapp.its_act'];
export const ITS_TRIAGE_PERMISSIONS = ['rbacapp.its_triage'];
export const ITS_AGENT_PERMISSIONS = ['rbacapp.its_triage', 'rbacapp.its_agent'];

export const STATUS_LABELS = {
    new: 'New',
    open: 'Open',
    pending: 'Pending',
    resolved: 'Resolved',
    closed: 'Closed',
    reopened: 'Reopened',
    cancelled: 'Cancelled',
};

export const STATUS_BADGE = {
    new: 'bg-sky/10 text-sky border border-sky/30',
    open: 'bg-primary/10 text-primary border border-primary/30',
    pending: 'bg-amber-500/10 text-amber-500 border border-amber-500/30',
    resolved: 'bg-success/10 text-success border border-success/30',
    closed: 'bg-bg-light text-text-secondary border border-border',
    reopened: 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/30',
    cancelled: 'bg-bg-light text-text-secondary border border-border',
};

// P1 is the loudest thing on the screen; P4 recedes.
export const PRIORITY_BADGE = {
    P1: 'bg-danger/10 text-danger border border-danger/40',
    P2: 'bg-amber-500/10 text-amber-500 border border-amber-500/40',
    P3: 'bg-sky/10 text-sky border border-sky/30',
    P4: 'bg-bg-light text-text-secondary border border-border',
};

// From the paper form — shown as priority-field help text.
export const PRIORITY_CRITERIA = {
    P1: 'Critical — a service is down, or the whole office/many users are blocked with no workaround.',
    P2: 'High — significant business impact, a deadline is at risk, or a workaround is painful.',
    P3: 'Medium — normal working is degraded but a workaround exists.',
    P4: 'Low — a question, a request, or a minor annoyance with no deadline.',
};

export const statusLabel = (ticket) =>
    ticket?.status_display || STATUS_LABELS[ticket?.status] || ticket?.status || '-';

export const statusBadgeClass = (status) =>
    STATUS_BADGE[status] || 'bg-bg-light text-text-secondary border border-border';

export const priorityBadgeClass = (priority) =>
    PRIORITY_BADGE[priority] || 'bg-bg-light text-text-secondary border border-border';

// effective_priority is assessed-if-set-else-requested — always show this.
export const effectivePriority = (ticket) =>
    ticket?.effective_priority || ticket?.assessed_priority || ticket?.requested_priority || null;

// True when IT overrode the requester's priority — must be surfaced, not silent.
export const priorityWasReassessed = (ticket) =>
    !!ticket?.assessed_priority
    && !!ticket?.requested_priority
    && ticket.assessed_priority !== ticket.requested_priority;

export const isOpenState = (status) => ['new', 'open', 'pending', 'reopened'].includes(status);

// Fields the requester may PATCH while status is 'new'.
export const REQUESTER_EDITABLE_FIELDS = ['description', 'additional_comments', 'phone', 'troubleshooting_availability'];

// Close/reopen/cancel are open to requester and agents alike.
export function getTicketActions(ticket, { isAgent = false, isRequester = false } = {}) {
    if (!ticket) return [];

    const status = ticket.status;
    const actions = [];
    const mine = isRequester || isAgent;

    if (isAgent) {
        if (status === 'new') actions.push('assign');
        if (isOpenState(status)) actions.push('set-priority', 'status');
        if (isOpenState(status)) actions.push('resolve');
        if (status !== 'new' && isOpenState(status)) actions.push('reassign');
    }

    if (mine && status === 'resolved') actions.push('close');
    if (mine && ['resolved', 'closed'].includes(status)) actions.push('reopen');
    if (mine && isOpenState(status)) actions.push('cancel');

    return actions;
}

// Reopen window is 14 days after closing; the backend is the real authority.
export function canReopen(ticket) {
    if (!ticket) return false;
    if (typeof ticket.can_reopen === 'boolean') return ticket.can_reopen;
    if (ticket.status !== 'closed') return ticket.status === 'resolved';
    if (!ticket.closed_at) return true;
    const days = (Date.now() - new Date(ticket.closed_at).getTime()) / (1000 * 60 * 60 * 24);
    return days <= 14;
}

// ─── SHARED HELPERS ──────────────────────────────────────────────────────────

export {
    classifyWorkflowError,
    isForbidden,
    withoutEmpty,
    formatDate,
    formatDateTime,
    dataTableStyles,
} from '../../lib/workflowUtils';
